import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@shared/auth/authContext'
import { ROLES } from '@shared/config/constants'
import { cn, timeAgo } from '@shared/lib'
import { EmptyState } from '@shared/ui'
import { buildBody } from '../lib/buildBody'
import {
  categoryToIconType,
  categoryToRoute,
  getCategoryLabelKey,
  isHiddenFromRepresentative,
  TAB_ALL,
  TAB_READ,
} from '../lib/helpers'
import { ModerationErrorKind } from '../lib/moderationError'
import { useNotificationsUi } from '../model/provider'
import type { NotificationCategory } from '../model/types'
import { useNotificationsData } from '../model/useNotificationsData'
import { NotificationCard } from './NotificationCard'

const ISSUE_NOTICE_KEYS = {
  [ModerationErrorKind.stale]: 'notification.error.stale',
  [ModerationErrorKind.resolved]: 'notification.error.resolved',
} as const

/** Категория или «Все» — только новые; `TAB_READ` — все уведомления роли, новые с точкой. */
export type NotificationsFilter = NotificationCategory | typeof TAB_ALL | typeof TAB_READ

interface NotificationsListProps {
  filter?: NotificationsFilter
  /** When filter is 'all' or 'read', restrict visible items to this category allow-list. */
  allowedCategories?: readonly NotificationCategory[]
  className?: string
}

export function NotificationsList({
  filter = TAB_ALL,
  allowedCategories,
  className,
}: NotificationsListProps) {
  const { t } = useTranslation('admin')
  const { role } = useAuth()
  const navigate = useNavigate()
  // Представитель не видит контакты участника ни в панели, ни во всплывающем окне.
  const hideField = role === ROLES.REPRESENTATIVE ? isHiddenFromRepresentative : undefined
  const { setOpen } = useNotificationsUi()
  const {
    notifications,
    newNotifications,
    isNew,
    approve,
    decline,
    isBusy,
    moderationIssues,
    fieldLabels,
    lang,
  } = useNotificationsData()

  const isRead = filter === TAB_READ
  const source = isRead ? notifications : newNotifications
  const items =
    filter === TAB_ALL || isRead
      ? allowedCategories
        ? source.filter((n) => allowedCategories.includes(n.type_display))
        : source
      : source.filter((n) => n.type_display === filter)

  if (items.length === 0)
    return (
      <EmptyState
        title={t(isRead ? 'notification.empty' : 'notification.empty_new')}
        className={className}
      />
    )

  return (
    <div className={cn('space-y-4', className)}>
      {items.map((n) => {
        const issue = moderationIssues[n.id]
        const route = n.actionable ? null : categoryToRoute(n.type_display, role)
        return (
          <NotificationCard
            key={n.id}
            iconType={categoryToIconType(n.type_display)}
            title={n.title}
            timeLabel={timeAgo(n.created_at, lang)}
            category={t(getCategoryLabelKey(n.type_display, role))}
            body={buildBody(n, fieldLabels, hideField)}
            navigable={!!route}
            onNavigate={
              route
                ? () => {
                    setOpen(false)
                    navigate(route)
                  }
                : undefined
            }
            status={n.localStatus}
            busy={isBusy(n.id)}
            notice={issue ? t(ISSUE_NOTICE_KEYS[issue]) : undefined}
            noticeNeutral={issue === ModerationErrorKind.resolved}
            approveDisabled={issue === ModerationErrorKind.stale}
            actionsHidden={issue === ModerationErrorKind.resolved}
            unread={isRead && isNew(n)}
            onApprove={n.actionable && !n.localStatus ? () => approve(n.id) : undefined}
            onDecline={n.actionable && !n.localStatus ? () => decline(n.id) : undefined}
          />
        )
      })}
    </div>
  )
}
